import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "../../theme/tokens";
import {
    getTankSpecificationsResults,
    getTankResults,
    getTowerSpecificationsResults,
    getWarmUpCalculations,
    getBudgetInputs,
    getCylindricalShellSection1Results,
    getCylindricalShellSection2Results,
    getCylindricalShellSection3Results,
    getDishedHeadSection1Results,
    getDishedHeadSection2Results,
    getDishedHeadSection3Results,
    getVacuumBreakResults,
    formatNumber
} from "../../services/helpers/Helpers";

const ExcelResultTable = ({ title, tableType, data, isTower }) => {

    const { t } = useTranslation();
    const [rows, setRows] = useState([]);

    useEffect(() => {

        if (!tableType) return;

        switch (tableType) {
            case "VESSEL":
                setRows(getTankSpecificationsResults(data));
                break;
            case "TOWER":
                setRows(getTowerSpecificationsResults(data));
                break;
            case "WARM_UP":
                setRows(getWarmUpCalculations(data));
                break;
            case "INPUTS":
                setRows(getBudgetInputs(data, t));
                break;
            case "TANK_CALCULATION":
                setRows(getTankResults(data, t));
                break;
            case "CYLINDRICAL_SHELL_1":
                setRows(getCylindricalShellSection1Results(data, isTower, t));
                break;
            case "CYLINDRICAL_SHELL_2":
                setRows(getCylindricalShellSection2Results(data, isTower, t));
                break;
            case "CYLINDRICAL_SHELL_3":
                setRows(getCylindricalShellSection3Results(data, isTower, t));
                break;
            case "DISHED_HEAD_1":
                setRows(getDishedHeadSection1Results(data, isTower, t));
                break;
            case "DISHED_HEAD_2":
                setRows(getDishedHeadSection2Results(data, isTower, t));
                break;
            case "DISHED_HEAD_3":
                setRows(getDishedHeadSection3Results(data, isTower, t));
                break;
            case "VACUUM_BREAKER":
                setRows(getVacuumBreakResults(data))
                break;
            default:
                setRows([]);
                break;
        }
    }, [tableType, data, isTower, t]);


    const displayValue = (value) => {
        if (value == null || value === "") return "—";
        if (/^-?\d+\.?\d*$/.test(String(value).trim())) {
            return formatNumber(value);
        }
        return value;
    };

    const Row = ({ row }) => (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                alignItems: "left",
                columnGap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 0.5,
                bgcolor: row.emphasis ? "#e3f2fd" : "transparent",
                transition: "background-color 0.15s ease",
                "&:hover": {
                    bgcolor: row.emphasis ? "#d0e4f5" : alpha.primary04,
                },
            }}
        >
            <Typography
                variant={row.title ? "body1" : "body2"}
                fontWeight={row.error || row.warning || row.title ? 700 : 400}
                sx={{
                    textDecoration: row.title ? "underline" : "none",
                    color:
                        row.error ? "error.main" :
                            row.warning ? "warning.main" :
                                "text.primary"
                }
                }
            >
                {row.label}
            </Typography>
            <Typography
                variant={"body2"}
                fontWeight={600}
                sx={{
                    color: row.error
                        ? "#FF0000"
                        : row.warning
                            ? "#FF0000"
                            : row.highlight2
                                ? "success.main"
                                : row.highlight
                                    ? "#ED7C2F"
                                    : row.ok
                                        ? "success.main"
                                        : "text.primary"
                }}
            >
                {displayValue(row.value)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                {row.unit}
            </Typography>
        </Box>
    );


    let leftRows = [];
    let rightRows = [];
    if (rows.length > 19 && tableType !== "VACUUM_BREAKER") {
        const mid = Math.ceil(rows.length / 2);
        leftRows = rows.slice(0, mid);
        rightRows = rows.slice(mid);
    } else if (tableType === "VACUUM_BREAKER") {
        leftRows = rows;
    }


    return (
        <Box>
            {title && (
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }} align="center">
                    {title}
                </Typography>
            )}

            <Stack spacing={0.5}>
                {rows.length > 19 && tableType !== "VACUUM_BREAKER" ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <Box>
                            {leftRows.map((row, index) => (
                                <Row key={`left-${index}`} row={row} />
                            ))}
                        </Box>
                        <Box>
                            {rightRows.map((row, index) => (
                                <Row key={`right-${index}`} row={row} />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    rows.map((row, index) => <Row key={index} row={row} />)
                )}
            </Stack>
        </Box>
    );
};

export default ExcelResultTable;
